import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastProps {
    toast: Toast;
    onClose: (id: string) => void;
}

const ToastItem: React.FC<ToastProps> = ({ toast, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);
    const duration = toast.duration || 4000;

    useEffect(() => {
        const startTime = Date.now();
        const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setProgress(remaining);
        }, 50);

        const timer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => {
            clearTimeout(timer);
            clearInterval(progressInterval);
        };
    }, [toast.id, duration]);

    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => onClose(toast.id), 200);
    };

    const config = {
        success: {
            icon: <CheckCircle className="w-[18px] h-[18px]" />,
            bg: 'bg-slate-900/95 backdrop-blur-lg',
            iconColor: 'text-emerald-400',
            progressBg: 'bg-emerald-400',
            ring: 'ring-1 ring-white/[0.06]',
        },
        error: {
            icon: <XCircle className="w-[18px] h-[18px]" />,
            bg: 'bg-slate-900/95 backdrop-blur-lg',
            iconColor: 'text-red-400',
            progressBg: 'bg-red-400',
            ring: 'ring-1 ring-white/[0.06]',
        },
        warning: {
            icon: <AlertTriangle className="w-[18px] h-[18px]" />,
            bg: 'bg-slate-900/95 backdrop-blur-lg',
            iconColor: 'text-amber-400',
            progressBg: 'bg-amber-400',
            ring: 'ring-1 ring-white/[0.06]',
        },
        info: {
            icon: <Info className="w-[18px] h-[18px]" />,
            bg: 'bg-slate-900/95 backdrop-blur-lg',
            iconColor: 'text-blue-400',
            progressBg: 'bg-blue-400',
            ring: 'ring-1 ring-white/[0.06]',
        }
    };

    const { icon, bg, iconColor, progressBg, ring } = config[toast.type];

    return (
        <div
            className={`
                relative overflow-hidden
                flex items-center gap-3 px-4 py-3
                min-w-[300px] max-w-sm
                ${bg} ${ring}
                rounded-xl shadow-2xl shadow-black/30
            `}
            style={{
                opacity: isExiting ? 0 : 1,
                transform: isExiting ? 'translateX(16px) scale(0.96)' : 'translateX(0) scale(1)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                animation: isExiting ? 'none' : 'toast-enter 0.35s cubic-bezier(0.16,1,0.3,1)',
            }}
            role="alert"
        >
            {/* Icon */}
            <div className={`flex-shrink-0 ${iconColor}`}>
                {icon}
            </div>

            {/* Message */}
            <p className="flex-1 text-[13px] font-medium text-slate-200 leading-snug">
                {toast.message}
            </p>

            {/* Close Button */}
            <button
                onClick={handleClose}
                className="flex-shrink-0 p-1 rounded-md text-slate-500 hover:text-slate-300 transition-colors"
                aria-label="Close notification"
            >
                <X size={14} />
            </button>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/[0.04]">
                <div 
                    className={`h-full ${progressBg} opacity-60`}
                    style={{ width: `${progress}%`, transition: 'width 100ms linear' }}
                />
            </div>
        </div>
    );
};

interface ToastContainerProps {
    toasts: Toast[];
    onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
    return (
        <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col gap-3">
                {toasts.map((toast, index) => (
                    <div 
                        key={toast.id} 
                        style={{ animationDelay: `${index * 0.05}s` }}
                    >
                        <ToastItem toast={toast} onClose={onClose} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ToastContainer;
